// Action Types
import AnalyticsTypes from '~/constants/types/AnalyticsTypes';
import IconTypes from '~/constants/types/IconTypes';
import UploadTypes from '~/constants/types/UploadTypes';
// Selectors
import {getIconById} from '~/selectors/CanvasSelectors';
// Utils
import {normalizeTagName} from '~/utils/analytics';
import {createCanvasRemoveEvents, isUploadIcon, hasUploadIcon} from './utils';

const Upload = {
  start: (_, {payload}) => {
    const {source, upload_type} = payload;
    if (!source) return null;
    return {
      event: 'lab_upload_start',
      source: source || '',
      upload_type: upload_type || ''
    };
  },
  add: (_, {payload}) => {
    const {action} = payload;
    return {
      event: 'lab_upload_add',
      upload_type: action || ''
    };
  },
  progress: (_, {payload}) => {
    const {upload_type} = payload;
    return {
      event: 'lab_upload_progress',
      upload_type: upload_type || ''
    };
  },
  success: (_, {payload}) => {
    const {colorMode, colorCount, extension, upload_type} = payload;
    return {
      event: 'lab_upload_success',
      upload_image_type: extension,
      upload_color_count: colorCount,
      upload_color_type: colorMode,
      upload_type: upload_type || ''
    };
  },
  failure: (_, {payload}) => {
    const {error, upload_type} = payload;
    return {
      event: 'lab_upload_failure',
      failure_message: error,
      upload_type: upload_type || ''
    };
  },
  recent: (state, {payload}) => {
    const icon = getIconById(state)(payload?.iconId);
    if (!icon) return;
    const {colorMode, spotColors} = icon;
    const {originalContentType} = payload;
    return {
      event: 'lab_upload_recent',
      upload_image_type: originalContentType,
      upload_color_count: spotColors?.length,
      upload_color_type: colorMode
    };
  },
  learnMore: () => {
    return {
      event: 'lab_upload_learn_more'
    };
  },
  contactUs: () => {
    return {
      event: 'lab_upload_contact_us'
    };
  },
  remove: (_, {payload}) => {
    return createCanvasRemoveEvents(payload, {
      targetIconType: 'UPLOAD'
    });
  },

  editToolsMakeOneColor: (state, {payload}) => {
    const {iconId, singleColor, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'make_one_color',
      tool_value: singleColor ? 'on' : 'off',
      tool_source: source
    };
  },
  editToolsEditColors: (_, {payload}) => {
    const {source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'edit_colors',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsSpotColor: (state, {payload}) => {
    const {iconId, user, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'edit_colors',
      tool_value: user?.name ? normalizeTagName(user?.name) : null,
      tool_source: source
    };
  },
  editToolsColor: (state, {payload}) => {
    const {color, iconId, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'edit_colors',
      tool_value: color?.name ? normalizeTagName(color?.name) : null,
      tool_source: source
    };
  },
  editToolsRemoveBackground: (_, {payload}) => {
    const {visible, source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'remove_background_color',
      tool_value: visible ? 'on' : 'off',
      tool_source: source
    };
  },
  editToolsSize: (_, {payload}) => {
    const {transforms, source} = payload;
    if (!source || !hasUploadIcon({transforms})) return null;
    const mapAdjustSources = {
      drag: 'canvas'
    };
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'resize',
      tool_value: null,
      tool_source: mapAdjustSources[source] || source
    };
  },
  editToolsRotation: (state, {payload}) => {
    const {iconId, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'rotate',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsPositionCenter: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasUploadIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'center',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerForward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasUploadIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'layer_forward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsLayerBackward: (state, {payload}) => {
    const {source, iconIds} = payload;
    if (!source || !hasUploadIcon({iconIds, getIconById, state})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'layer_backward',
      tool_value: null,
      tool_source: source
    };
  },
  editToolsHorizontalFlip: (state, {payload}) => {
    const {flip, iconId, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'flip_h',
      tool_value: flip,
      tool_source: source
    };
  },
  editToolsVerticalFlip: (state, {payload}) => {
    const {flip, iconId, source} = payload;
    if (!source || !isUploadIcon({getIconById, state, iconId})) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'flip_v',
      tool_value: flip,
      tool_source: source
    };
  },
  editToolsResetToOriginal: (_, {payload}) => {
    const {source} = payload;
    if (!source) return null;
    return {
      event: 'lab_edit_tools',
      tool_icon_type: 'upload',
      tool_name: 'reset_to_original',
      tool_value: null,
      tool_source: source
    };
  }
};

const UploadEvents = {
  [AnalyticsTypes.UPLOAD_START]: Upload.start,
  [AnalyticsTypes.UPLOAD_ADD]: Upload.add,
  [UploadTypes.UPLOAD_IN_PROGRESS]: Upload.progress,
  [AnalyticsTypes.UPLOAD_SUCCESS]: Upload.success,
  [UploadTypes.UPLOAD_FAILED]: Upload.failure,
  [UploadTypes.INVALID_FILE]: Upload.failure,
  [AnalyticsTypes.UPLOAD_RECENT]: Upload.recent,
  [AnalyticsTypes.UPLOAD_LEARN_MORE]: Upload.learnMore,
  [AnalyticsTypes.UPLOAD_CONTACT_US]: Upload.contactUs,
  [IconTypes.REMOVE]: Upload.remove,
  [IconTypes.UPDATE_SINGLE_COLOR]: Upload.editToolsMakeOneColor,
  [IconTypes.CHANGE_COLOR]: Upload.editToolsColor,
  [UploadTypes.CHANGE_SPOT_COLOR]: Upload.editToolsSpotColor,
  [AnalyticsTypes.UPLOAD_TOGGLE_BACKGROUND]: Upload.editToolsRemoveBackground,
  [IconTypes.RESIZE]: Upload.editToolsSize,
  [IconTypes.ROTATE]: Upload.editToolsRotation,
  [IconTypes.CENTER]: Upload.editToolsPositionCenter,
  [IconTypes.FORWARD]: Upload.editToolsLayerForward,
  [IconTypes.BACKWARD]: Upload.editToolsLayerBackward,
  [IconTypes.ALIGN]: Upload.editToolsAlignment,
  [IconTypes.UPDATE_HORIZONTAL_FLIP]: Upload.editToolsHorizontalFlip,
  [IconTypes.UPDATE_VERTICAL_FLIP]: Upload.editToolsVerticalFlip,
  [UploadTypes.RESET_UPLOAD]: Upload.editToolsResetToOriginal
};

export default UploadEvents;
